/**
 * Protected Route Component
 * 
 * Feature: 033-auth-complete
 * Description: Route guard that redirects unauthorized users
 * Constitution: v1.11.0
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { UserRole } from '@shared/types/auth';
import type { License } from '@shared/access/licenses';
import { canAccessArea, type NavAreaId } from '@shared/access/navAccessPolicy';
import { principalHasLicense } from '../hooks/useAccess';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: UserRole | 'superadmin' | 'SuperAdmin';
  /** Spec 285 — require a tenant license (SuperAdmin is exempt). */
  requiredLicense?: License;
  /**
   * Spec 285 — require access to a gated navigation area, decided by the SAME shared
   * policy the backend's requireNavAccess enforces (role set ∧ license, SuperAdmin full),
   * so a deep link to a hidden page is refused exactly as its API would be.
   */
  requiredArea?: NavAreaId;
  requiredPermission?: string;
}

/**
 * Spec 216 T007 (R1 gap fix): the shared System-A `UserRole` union predates the
 * Manager role, so `manager` users scored `undefined` here and were bounced to
 * /unauthorized by any guarded route. The hierarchy is keyed loosely and ranks
 * all four live roles; unknown roles score 0 (deny). Collapsing the parallel
 * UserRole definitions themselves is Spec 190.
 */
export const ROLE_HIERARCHY: Record<string, number> = {
  viewer: 1,
  operator: 2,
  manager: 3,
  admin: 4,
  // Spec 285 — SuperAdmin is now an ENFORCED role (the platform-administration tier),
  // ranked above Admin. It remains a context selector, not a cross-tenant bypass (ADR 0091).
  superadmin: 5,
};

/**
 * Protected Route wrapper
 * Redirects to /unauthorized if user lacks required role or permission
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
  requiredLicense,
  requiredArea,
  requiredPermission,
}) => {
  const { user, isAuthenticated, isLoading, hasPermission, licenses } = useAuthStore();
  const location = useLocation();

  // Show loading state while auth is initializing
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg text-gray-600">Loading...</div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role requirement. NB: the hierarchy is keyed lowercase, but the canonical
  // runtime role value is PascalCase (e.g. 'Admin') — so both sides MUST be lowercased
  // before the lookup, or an Admin scores `undefined` (→ 0) and gets bounced from every
  // AdminOnly route (owner-reported 2026-07-06). Case-insensitive is the durable fix
  // until Spec 190 collapses the parallel UserRole definitions.
  if (requiredRole) {
    const userLevel = ROLE_HIERARCHY[String(user.role).toLowerCase()] ?? 0;
    const requiredLevel = ROLE_HIERARCHY[String(requiredRole).toLowerCase()] ?? Number.MAX_SAFE_INTEGER;

    if (userLevel < requiredLevel) {
      return <Navigate to="/unauthorized" state={{ requiredRole }} replace />;
    }
  }

  // Spec 285 — license requirement (SuperAdmin exempt). Visibility must equal reachability:
  // the backend re-decides, but the SPA must not render a page it cannot populate.
  if (requiredLicense && !principalHasLicense(user.role, licenses, requiredLicense)) {
    return <Navigate to="/unauthorized" state={{ requiredLicense }} replace />;
  }

  // Spec 285 — gated navigation area (evaluated only after loading + auth above, so the
  // session's licenses have arrived before the decision is made).
  if (requiredArea && !canAccessArea(requiredArea, { role: user.role, licenses })) {
    return <Navigate to="/unauthorized" state={{ requiredArea }} replace />;
  }

  // Check permission requirement
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return <Navigate to="/unauthorized" state={{ requiredPermission }} replace />;
  }

  return <>{children}</>;
};
